import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import { FOCUSABLE, trapFocus } from "../dialogFocus";
import "../styles/learning-editor.css";
import { CATALOG_TEXT } from "./catalogText";

const OPEN_KEYS = Object.freeze(["Enter", " "]);

/** Props that make a table row open its detail popup: focusable, opened by a click, Enter or Space. */
export const rowOpener = (open) => ({
  tabIndex: 0,
  "aria-haspopup": "dialog",
  onClick: open,
  onKeyDown: (event) => {
    if (event.target !== event.currentTarget || !OPEN_KEYS.includes(event.key)) return;
    event.preventDefault();
    open();
  },
});

/**
 * The Learning Catalog's detail popup, the same dialog shell as the Learning editor (le-* tokens): a modal labelled by its
 * title. Focus moves into it and stays there (Tab cycles); Esc, the close button or a click on the backdrop closes it, and
 * focus goes back to what opened it (the table row).
 */
export default function DetailModal({ title, onClose, children }) {
  const panelRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    const previous = document.activeElement;
    const panel = panelRef.current;
    const first = panel && panel.querySelector(FOCUSABLE);
    if (first) first.focus();
    else if (panel) panel.focus();
    return () => { if (previous && previous.isConnected && typeof previous.focus === "function") previous.focus(); };
  }, []);

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
      return;
    }
    trapFocus(event, panelRef.current);
  };
  const onBackdrop = (event) => { if (event.target === event.currentTarget) onClose(); };

  const tree = (
    <div className="le-overlay" onKeyDown={onKeyDown} onMouseDown={onBackdrop}>
      <div className="le-dialog le-dialog--wide" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={panelRef} tabIndex={-1}>
        <header className="le-header">
          <h2 className="le-title" id={titleId}>{title}</h2>
          <span className="le-spacer" />
          <button type="button" className="le-icon-btn" onClick={onClose} aria-label={CATALOG_TEXT.detail.close}>
            <X size={16} aria-hidden="true" />
          </button>
        </header>
        <div className="le-body">{children}</div>
      </div>
    </div>
  );
  return typeof document === "undefined" ? tree : createPortal(tree, document.body);
}
